/**
 * Configurable Goals taxonomy.
 *
 * goalType / goalFramework / planningPeriod / level are CONFIGURABLE: their
 * allowed values are defined here (not baked into the type system) so the
 * taxonomy can grow without a code change to the domain types. Each list ends
 * with a "Custom" option so a team can capture something outside the defaults.
 *
 * "Not every level is required" — the hierarchy levels below are an ordered
 * menu, not a mandatory chain. A goal may sit at any level and link to any
 * parent regardless of level.
 *
 * Pure TS — no React. Presentation layers map these to <Select> options.
 */

/** A single configurable taxonomy entry. */
export interface GoalConfigOption {
  /** Stored value (also the option value). */
  value: string;
  /** Human-readable label. */
  label: string;
  /** Optional one-line helper shown in pickers. */
  hint?: string;
}

/** Sentinel value shared by every list's "Custom / other" escape hatch. */
export const CUSTOM_VALUE = 'Custom';

/**
 * Goal frameworks — the planning framework a goal is expressed in. Covers the
 * BHAG / Quarterly / OP1 / OP2 / Annual planning constructs plus Custom.
 */
export const GOAL_FRAMEWORKS: GoalConfigOption[] = [
  { value: 'BHAG', label: 'BHAG', hint: 'Big Hairy Audacious Goal (multi-year)' },
  { value: 'OP1', label: 'OP1', hint: 'Operating Plan 1 (annual plan)' },
  { value: 'OP2', label: 'OP2', hint: 'Operating Plan 2 (annual refresh)' },
  { value: 'Annual', label: 'Annual', hint: 'Yearly objective' },
  { value: 'Quarterly', label: 'Quarterly', hint: 'Quarter-scoped objective' },
  { value: CUSTOM_VALUE, label: 'Custom', hint: 'Framework outside the defaults' },
];

/**
 * Goal types — the scope/kind of the goal. Covers Organization / Team plus
 * common intermediate scopes, and Custom.
 */
export const GOAL_TYPES: GoalConfigOption[] = [
  { value: 'Organization', label: 'Organization', hint: 'Org-wide objective' },
  { value: 'Program', label: 'Program', hint: 'Cross-team program' },
  { value: 'Team', label: 'Team', hint: 'Single-team objective' },
  { value: 'Initiative', label: 'Initiative', hint: 'A workstream / initiative' },
  { value: CUSTOM_VALUE, label: 'Custom', hint: 'Type outside the defaults' },
];

/**
 * Planning periods — Q1..Q4 / H1 / H2 / Year / Custom.
 */
export const PLANNING_PERIODS: GoalConfigOption[] = [
  { value: 'Q1', label: 'Q1' },
  { value: 'Q2', label: 'Q2' },
  { value: 'Q3', label: 'Q3' },
  { value: 'Q4', label: 'Q4' },
  { value: 'H1', label: 'H1', hint: 'First half' },
  { value: 'H2', label: 'H2', hint: 'Second half' },
  { value: 'Year', label: 'Year', hint: 'Full year' },
  { value: CUSTOM_VALUE, label: 'Custom', hint: 'Period outside the defaults' },
];

/**
 * Hierarchy levels, ordered broad → narrow. Not every level is required; a goal
 * may occupy any single level. The ordering is used only to sort/indent the
 * tree sensibly when two sibling goals declare different levels.
 *
 *   Organization > Program / Org > Team BHAG > Quarterly > Initiative / Epic > Sprint Story
 */
export const GOAL_LEVELS: GoalConfigOption[] = [
  { value: 'Organization', label: 'Organization' },
  { value: 'Program', label: 'Program / Org' },
  { value: 'TeamBHAG', label: 'Team BHAG' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'InitiativeEpic', label: 'Initiative / Epic' },
  { value: 'SprintStory', label: 'Sprint Story' },
  { value: CUSTOM_VALUE, label: 'Custom' },
];

/** Default selections for a brand-new goal draft. */
export const DEFAULT_GOAL_FRAMEWORK = 'Quarterly';
export const DEFAULT_GOAL_TYPE = 'Team';
export const DEFAULT_PLANNING_PERIOD = 'Q1';
export const DEFAULT_GOAL_LEVEL = 'Quarterly';

/** Ordinal rank of a level for tree sorting (lower = broader). Unknown = end. */
export function levelRank(level: string): number {
  const idx = GOAL_LEVELS.findIndex((l) => l.value === level);
  return idx === -1 ? GOAL_LEVELS.length : idx;
}

/** Human-readable label for any configurable value (falls back to the raw value). */
export function optionLabel(
  options: GoalConfigOption[],
  value: string,
): string {
  return options.find((o) => o.value === value)?.label ?? value;
}
